import React, { useContext, useEffect, useState } from "react";
import { APIContext } from "../../../Contexts/APIContext";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { InputProps } from "../Interfaces/InputProps";
import { InputType } from "../Enums/InputType";
import ReportCodeFiltersViewModel from "./ViewModels/ReportCodeFiltersViewModel";
import CallTableViewModel from "../../../ViewModels/CallTableViewModel";
import Chip from "@mui/material/Chip";

interface IRepCodeFiltersDialogProps {
    open: boolean
    setOpen: (value: boolean) => void
    callTableViewModel: CallTableViewModel
    setCallTableViewModel: (value: CallTableViewModel) => void
}

export default function ReportCodeFiltersDialog({ open, setOpen, callTableViewModel, setCallTableViewModel }: IRepCodeFiltersDialogProps) {
    const { get } = useContext(APIContext);
    const [reportCodeFilters, setReportCodeFilters] = useState<ReportCodeFiltersViewModel>(callTableViewModel.filters.reportCodeFilters);
    const [repCode1Options, setRepCode1Options] = useState<string[]>([]);
    const [repCode2Options, setRepCode2Options] = useState<string[]>([]);
    const [repCode3Options, setRepCode3Options] = useState<string[]>([]);
    const [repCode5Options, setRepCode5Options] = useState<string[]>([]);

    useEffect(() => {
        if (open) {
            setReportCodeFilters(callTableViewModel.filters.reportCodeFilters);
        }
    }, [open])

    useEffect(() => {
        if (callTableViewModel.filters.client) {
            //dates may come back from a saved/preset filter as plain strings rather than Date objects, so they must be re-wrapped before calling Date methods on them
            const startDate = new Date(callTableViewModel.filters.dateTimeFilters.startDate).toUTCString();
            const endDate = new Date(callTableViewModel.filters.dateTimeFilters.endDate).toUTCString();

            get('search', `GetReportcode1DropDown?client=${callTableViewModel.filters.client}&dateFlag=${callTableViewModel.filters.dateTimeFilters.preset}&startDate=${startDate}&endDate=${endDate}`, (data) => setRepCode1Options(data));
            get('search', `GetReportcode2DropDown?client=${callTableViewModel.filters.client}&dateFlag=${callTableViewModel.filters.dateTimeFilters.preset}&startDate=${startDate}&endDate=${endDate}`, (data) => setRepCode2Options(data));
            get('search', `GetReportcode3DropDown?client=${callTableViewModel.filters.client}&dateFlag=${callTableViewModel.filters.dateTimeFilters.preset}&startDate=${startDate}&endDate=${endDate}`, (data) => setRepCode3Options(data));
            get('search', `GetReportcode5DropDown?client=${callTableViewModel.filters.client}&dateFlag=${callTableViewModel.filters.dateTimeFilters.preset}&startDate=${startDate}&endDate=${endDate}`, (data) => setRepCode5Options(data));
        }
    }, [callTableViewModel.filters.client, callTableViewModel.filters.dateTimeFilters])

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    const inputProps: InputProps[] = [
        {
            name: 'reportCode1',
            label: 'Reportcode 1',
            value: reportCodeFilters.reportCode1.values,
            options: repCode1Options,
            type: InputType.AutoComplete
        },
        {
            name: 'reportCode2',
            label: 'Reportcode 2',
            value: reportCodeFilters.reportCode2.values,
            options: repCode2Options,
            type: InputType.AutoComplete
        },
        {
            name: 'reportCode3',
            label: 'Reportcode 3',
            value: reportCodeFilters.reportCode3.values,
            options: repCode3Options,
            type: InputType.AutoComplete
        },
        {
            name: 'reportCode5',
            label: 'Reportcode 5',
            value: reportCodeFilters.reportCode5.values,
            options: repCode5Options,
            type: InputType.AutoComplete
        },
    ];

    const handleAutoCompleteChange = (repCodeNumber: string, value: string[]) => {
        const repCode = { ...reportCodeFilters[repCodeNumber] };
        repCode.values = value;

        setReportCodeFilters({
            ...reportCodeFilters,
            [repCodeNumber]: repCode
        })
    }

    const handleExcludeChange = (repCodeNumber: string) => {
        const repCode = { ...reportCodeFilters[repCodeNumber] };
        repCode.excluded = !repCode.excluded;
        setReportCodeFilters({
            ...reportCodeFilters,
            [repCodeNumber]: repCode
        })
    }

    const confirm = () => {
        const newCallTableViewModel = { ...callTableViewModel };

        newCallTableViewModel.filters.reportCodeFilters = reportCodeFilters;

        setCallTableViewModel({
            ...newCallTableViewModel
        })

        setOpen(false);
    }

    return (
        <Dialog onClose={() => setOpen(false)} open={open} fullWidth maxWidth="sm">
            <DialogTitle>Report Filters</DialogTitle>
            <DialogContent>
                <Grid container height="100%">
                    {
                        inputProps.map(props =>
                            <Grid key={props.name} size={12}>
                                <Grid>
                                    <FormControlLabel control={<Checkbox checked={reportCodeFilters[props.name].excluded} onChange={() => handleExcludeChange(props.name)} />} label="Exclude" />
                                </Grid>
                                <Grid>
                                    <InputLabel>{props.label}</InputLabel>
                                    {
                                        props.type === InputType.AutoComplete &&
                                        <Autocomplete
                                            multiple
                                            fullWidth
                                            options={props.options!}
                                            disableCloseOnSelect
                                            getOptionLabel={(option) => option}
                                            value={props.value as string[]}
                                            onChange={(event: any, newValue: string[]) => { handleAutoCompleteChange(props.name, newValue) }}
                                            limitTags={3}
                                            renderOption={(optionProp, option, { selected }) => {
                                                const { ...optionProps } = optionProp;
                                                return (
                                                    <li {...optionProps}>
                                                        <Checkbox
                                                            icon={icon}
                                                            checkedIcon={checkedIcon}
                                                            style={{ marginRight: 8 }}
                                                            checked={selected}
                                                        />
                                                        {option}
                                                    </li>
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField {...params} fullWidth />
                                            )}
                                            renderTags={(value, getTagProps) => {
                                                const numTags = value.length;
                                                const limitTags = 1;
                                                return (
                                                    <>
                                                        {value.slice(0, limitTags).map((option, index) => (
                                                            <Chip
                                                                {...getTagProps({ index })}
                                                                key={index}
                                                                label={option}
                                                            />
                                                        ))}

                                                        {numTags > limitTags && ` +${numTags - limitTags}`}
                                                    </>
                                                );
                                            }}
                                        />
                                    }
                                </Grid>
                            </Grid>)
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>
                    Cancel
                </Button>
                <Button onClick={confirm}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    )
}